import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import SectionTitle from "../../../components/shared/SectionTitle";
import AceAvatar from "../../../components/shared/AceAvatar";
import StressDot from "../../../components/ui/StressDot";
import StatCard from "../../../components/ui/StatCard";
import UCPageShell from "../../../components/ui/UCPageShell";
import {
  fetchClasses,
  fetchUserDetails,
} from "../../../services/usersClassesService";
import { canAccessUserRecord } from "../../../utils/auth";

function InfoCard({ label, children }) {
  return (
    <div className="ac-soft-panel ac-uc-detail-infocard">
      <div className="ac-uc-detail-label">{label}</div>
      <div className="mt-3">{children}</div>
    </div>
  );
}

export default function UserDetailsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const backTo =
    location.state?.backTo || (location.state?.fromStats ? "/stats" : "/users-classes/users");

  const backLabel =
    location.state?.backLabel ||
    (location.state?.fromStats ? "← Zurück zu Statistiken" : "← Zurück zu Nutzer:innen");

  const [details, setDetails] = useState(null);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    async function load() {
      try {
        const [detailData, classData] = await Promise.all([
          fetchUserDetails(id),
          fetchClasses(),
        ]);

        if (!mounted) return;
        setDetails(detailData);
        setClasses(classData);
      } catch (error) {
        console.error("User-Details konnten nicht geladen werden:", error);
        if (!mounted) return;
        setDetails(null);
        setClasses([]);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();

    return () => {
      mounted = false;
    };
  }, [id]);

  const user = details?.user || null;
  const klass = useMemo(
    () => classes.find((entry) => entry.name === user?.className) || null,
    [classes, user]
  );

const hasAccess = useMemo(() => {
  if (!user) return false;
  return canAccessUserRecord(user);
}, [user]);

  const aceVariant = useMemo(() => {
    if (!user) return "motiviert1";
    if (Number(details?.averageStress || 0) >= 4) return "stress2";
    if (Number(details?.averageStress || 0) === 3) return "ruhig1";
    return "motiviert1";
  }, [user, details]);

  const statusHint = useMemo(() => {
    const avg = Number(details?.averageStress || 0);

    if (avg >= 4) {
      return "Dieses Profil zeigt aktuell erhöhte Belastungswerte. Ein Blick auf Verlauf, Aufgaben und letzte Aktivitäten wäre sinnvoll.";
    }
    if (avg === 3) {
      return "Aktuell wirkt die Nutzung eher gemischt, aber noch stabil beobachtbar.";
    }
    return "Sehr stabile Entwicklung und insgesamt ruhige Nutzung.";
  }, [details]);

  if (loading) {
    return (
      <UCPageShell title="User-Details">
        <div className="ac-soft-panel ac-uc-block">
          <div className="text-sm text-ac-text">Lade User-Details…</div>
        </div>
      </UCPageShell>
    );
  }

  if (!user || !hasAccess) {
    return (
      <UCPageShell title="User-Details">
        <div className="ac-soft-panel ac-uc-block">
          <div className="text-sm text-ac-text">User nicht gefunden.</div>
          <button
            type="button"
            onClick={() => navigate(backTo)}
            className="mt-4 ac-primarybtn"
          >
            {backLabel}
          </button>
        </div>
      </UCPageShell>
    );
  }

  return (
    <UCPageShell
      title="User-Details"
      right={
        <button
          type="button"
          onClick={() => navigate(backTo)}
          className="ac-ghostbtn"
        >
          {backLabel}
        </button>
      }
    >
      <div className="space-y-5">
        <section className="ac-soft-panel ac-uc-hero">
          <div className="ac-uc-hero-grid">
            <div>
              <h2 className="ac-uc-hero-title">{user.displayName}</h2>
              <p className="ac-uc-hero-text">
                Hier findest du alle relevanten Informationen zur Person inklusive
                Aktivität, Belastungsstatus und letzter Nutzung.
              </p>
            </div>

            <div className="ac-uc-hero-side">
              <div className="ac-uc-acebox">
                <AceAvatar variant={aceVariant} size={56} />
                <div className="ac-uc-acebubble">
                  Ace sagt: Dieses Profil zeigt dir Aktivität, Status und wichtige
                  Hinweise auf einen Blick.
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="ac-soft-panel ac-uc-block">
          <SectionTitle
            title="Profilübersicht"
            subtitle="Grunddaten, Aktivität und aktueller Belastungsstatus"
          />

          <div className="mt-5 grid grid-cols-1 lg:grid-cols-3 gap-4">
            <InfoCard label="Person">
              <div className="text-lg font-extrabold text-ac-text">{user.displayName}</div>
              <div className="mt-2 text-sm text-ac-muted">
                {user.email || "Keine E-Mail gespeichert"}
              </div>
              <div className="mt-3 text-sm text-ac-text">
                <span className="font-extrabold">Klasse:</span>{" "}
                {klass?.name ?? user.className}
              </div>
              <div className="mt-1 text-sm text-ac-text">
                <span className="font-extrabold">Registriert:</span>{" "}
                {user.createdAtFormatted}
              </div>
            </InfoCard>

            <InfoCard label="Aktivität">
              <div className="text-sm text-ac-text">
                <div>
                  <span className="font-extrabold">Zuletzt online:</span>{" "}
                  {user.lastActiveLabel ?? "—"}
                </div>
                <div className="mt-2">
                  <span className="font-extrabold">Fokusminuten:</span>{" "}
                  {details.totalFocusMinutes}
                </div>
              </div>
            </InfoCard>

            <InfoCard label="Belastungsstatus">
              <div className="flex items-center gap-3">
                <StressDot stress={details.averageStress} />
              </div>
              <div className="mt-3 text-sm text-ac-text">{statusHint}</div>
            </InfoCard>
          </div>
        </section>

        <section className="ac-soft-panel ac-uc-block">
          <SectionTitle
            title="Nutzungskennzahlen"
            subtitle="Wichtige Werte aus der bisherigen App-Nutzung"
          />

          <div className="mt-5 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            <StatCard
              title="Stress-Logs"
              value={details.stressLogs.length}
              hint="gesamt"
              iconText="🧠"
            />
            <StatCard
              title="Offene Aufgaben"
              value={details.openTasks}
              hint="aktuell offen"
              iconText="📝"
            />
            <StatCard
              title="Erledigte Aufgaben"
              value={details.completedTasks}
              hint="gesamt"
              iconText="✅"
            />
            <StatCard
              title="Fokusminuten"
              value={details.totalFocusMinutes}
              hint="gesamt"
              iconText="🎯"
            />
          </div>
        </section>

        <section className="ac-soft-panel ac-uc-block">
          <div className="ac-uc-detail-callout">
            <div>
              <div className="ac-uc-detail-callout-title">Admin-Hinweis</div>
              <div className="ac-uc-detail-callout-text">{statusHint}</div>
            </div>

            <button
              type="button"
              onClick={() =>
                navigate(`/users-classes/classes/${klass?.id || user.className}`, {
                  state: {
                    ...location.state,
                    backTo,
                    backLabel,
                  },
                })
              }
              className="ac-primarybtn ac-uc-smallbtn"
            >
              Klasse öffnen
            </button>
          </div>
        </section>

        <section className="ac-soft-panel ac-uc-block">
          <SectionTitle
            title="Systemhinweis"
            subtitle="Automatisch erzeugte Zusatzinformation zum Profil"
          />

          <div className="ac-uc-systemhint">
            <div className="ac-uc-systemhint-head">
              <span className="ac-uc-systemhint-dot" aria-hidden="true" />
              <span className="ac-uc-systemhint-label">Systemanalyse</span>
            </div>

            <div className="ac-uc-systemhint-text">
              {details.averageStress >= 4
                ? "Mehrere Hinweise deuten auf erhöhte Belastung hin. Bitte Verlauf und Aufgaben prüfen."
                : details.averageStress === 3
                ? "Es gibt leichte Auffälligkeiten. Beobachtung sinnvoll."
                : "Aktuell keine kritischen Auffälligkeiten erkannt."}
            </div>
          </div>
        </section>

        <section className="ac-soft-panel ac-uc-block">
          <SectionTitle
            title="Letzte Aktivitäten"
            subtitle="Zuletzt erkannte Einträge im Zusammenhang mit diesem Profil"
          />

          <div className="mt-4 space-y-3">
            {details.recentLogs.length > 0 ? (
              details.recentLogs.map((log, idx) => (
                <div key={idx} className="ac-uc-logitem">
                  <div className="ac-uc-logtime">{log.t}</div>
                  <div className="ac-uc-logtext">{log.text}</div>
                </div>
              ))
            ) : (
              <div className="ac-uc-empty">Noch keine Aktivitäten vorhanden.</div>
            )}
          </div>
        </section>
      </div>
    </UCPageShell>
  );
}